import { shouldInterceptHtmlNavigation } from "./shouldInterceptHtmlNavigation.ts";

export interface LeaveGuardOptions {
  /** Injected activity predicate — replaces the old `body.game-active` CSS probe. */
  isActive: () => boolean;
  isResumable: () => boolean;
  saveGame: () => void;
  requestConfirmedExit: () => void;
}

export interface LeaveGuardHandle {
  /**
   * Disarm for an intentional leave that will unload the page (reload / assign).
   * Removes listeners without unwinding history — safe before navigation.
   */
  allowUnload: () => void;
  /** Soft session teardown: remove listeners and unwind sentinel history entries. */
  dispose: () => void;
  pendingNavigationUrl: string | null;
  completePendingNavigation: () => void;
}

export function createLeaveGuard(options: LeaveGuardOptions): LeaveGuardHandle {
  let pendingNavigationUrl: string | null = null;
  let historyGuardDepth = 0;
  let armed = true;

  const shouldGuard = (): boolean =>
    armed && options.isActive() && options.isResumable();

  const pushHistoryGuard = (): void => {
    window.history.pushState({ dawnGameActive: true }, "");
    historyGuardDepth += 1;
  };

  const beforeUnloadHandler = (event: BeforeUnloadEvent): void => {
    if (!shouldGuard()) {
      return;
    }

    event.preventDefault();
    event.returnValue = "";
  };

  const pageHideHandler = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      return;
    }

    if (!shouldGuard()) {
      return;
    }

    options.saveGame();
  };

  const popStateHandler = (): void => {
    if (!shouldGuard()) {
      return;
    }

    pushHistoryGuard();
    options.requestConfirmedExit();
  };

  const linkClickHandler = (event: MouseEvent): void => {
    if (!shouldGuard()) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const anchor = target.closest("a[href]");
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }

    const url = new URL(anchor.href, window.location.href);
    if (
      !shouldInterceptHtmlNavigation(
        { origin: window.location.origin, pathname: window.location.pathname },
        { origin: url.origin, pathname: url.pathname },
      )
    ) {
      return;
    }

    event.preventDefault();
    pendingNavigationUrl = url.href;
    options.requestConfirmedExit();
  };

  const removeListeners = (): void => {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    window.removeEventListener("pagehide", pageHideHandler);
    window.removeEventListener("popstate", popStateHandler);
    document.removeEventListener("click", linkClickHandler, true);
  };

  window.addEventListener("beforeunload", beforeUnloadHandler);
  window.addEventListener("pagehide", pageHideHandler);
  window.addEventListener("popstate", popStateHandler);
  document.addEventListener("click", linkClickHandler, true);
  pushHistoryGuard();

  return {
    get pendingNavigationUrl() {
      return pendingNavigationUrl;
    },
    completePendingNavigation(): void {
      if (!pendingNavigationUrl) {
        return;
      }

      const nextUrl = pendingNavigationUrl;
      pendingNavigationUrl = null;
      window.location.assign(nextUrl);
    },
    allowUnload(): void {
      armed = false;
      removeListeners();
    },
    dispose(): void {
      armed = false;
      removeListeners();
      pendingNavigationUrl = null;

      if (historyGuardDepth > 0) {
        window.history.go(-historyGuardDepth);
        historyGuardDepth = 0;
      }
    },
  };
}
